import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { API, useAuth, authFetch } from "@/App";
import AlbumCard from "@/components/AlbumCard";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { LayoutList, ArrowLeft, Globe, Lock, Plus, Trash2, Users, UserPlus, X, Search } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

export default function ListDetail() {
  const { listId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [allAlbums, setAllAlbums] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [collabDialogOpen, setCollabDialogOpen] = useState(false);
  const [collabSearch, setCollabSearch] = useState("");
  const [collabResults, setCollabResults] = useState([]);
  const [searchingCollabs, setSearchingCollabs] = useState(false);

  const isOwner = user && list && user.user_id === list.user_id;
  const isCollaborator = user && list && (list.collaborators || []).includes(user.user_id);
  const canEdit = isOwner || isCollaborator;

  useEffect(() => {
    fetchData();
  }, [listId]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [listRes, albumsRes] = await Promise.all([
        authFetch(`${API}/lists/${listId}`),
        authFetch(`${API}/albums`),
      ]);

      if (listRes.ok) {
        const listData = await listRes.json();
        setList(listData);
      }

      if (albumsRes.ok) {
        const albumsData = await albumsRes.json();
        setAllAlbums(albumsData);
      }
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddAlbum = async (albumId) => {
    try {
      const response = await authFetch(`${API}/lists/${listId}/albums/${albumId}`, {
        method: "POST",
      });
      if (response.ok) {
        toast.success("Album added to list!");
        fetchData();
        setAddDialogOpen(false);
      } else {
        toast.error("Failed to add album");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const handleRemoveAlbum = async (albumId) => {
    try {
      const response = await authFetch(`${API}/lists/${listId}/albums/${albumId}`, {
        method: "DELETE",
      });
      if (response.ok) {
        toast.success("Album removed");
        fetchData();
      } else {
        toast.error("Failed to remove album");
      }
    } catch (error) {
      toast.error("Something went wrong");
    }
  };

  const getAvailableAlbums = () => {
    const existingIds = new Set((list?.album_ids || []).map((id) => id));
    return allAlbums.filter((album) => !existingIds.has(album.album_id));
  };

  const getFilteredAlbums = () => {
    const available = getAvailableAlbums();
    if (!searchQuery) return available;
    const query = searchQuery.toLowerCase();
    return available.filter(
      (album) => album.title.toLowerCase().includes(query) || album.artist.toLowerCase().includes(query)
    );
  };

  const searchUsers = async (q) => {
    if (!q.trim()) { setCollabResults([]); return; }
    setSearchingCollabs(true);
    try {
      const res = await authFetch(`${API}/users/search?q=${encodeURIComponent(q)}&limit=10`);
      if (res.ok) {
        const data = await res.json();
        // Filter out owner and existing collaborators
        const existing = new Set([list.user_id, ...(list.collaborators || [])]);
        setCollabResults(data.filter(u => !existing.has(u.user_id)));
      }
    } catch { /* ignore */ }
    finally { setSearchingCollabs(false); }
  };

  const addCollaborator = async (userId) => {
    try {
      const res = await authFetch(`${API}/lists/${listId}/collaborators`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ user_id: userId }),
      });
      if (res.ok) {
        toast.success("Collaborator added!");
        fetchData();
        setCollabSearch("");
        setCollabResults([]);
      } else {
        const err = await res.json();
        toast.error(err.detail || "Failed to add collaborator");
      }
    } catch { toast.error("Something went wrong"); }
  };

  const removeCollaborator = async (userId) => {
    try {
      const res = await authFetch(`${API}/lists/${listId}/collaborators/${userId}`, {
        method: "DELETE",
      });
      if (res.ok) {
        toast.success("Collaborator removed");
        fetchData();
      } else {
        toast.error("Failed to remove collaborator");
      }
    } catch { toast.error("Something went wrong"); }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background pt-36">
        <div className="max-w-4xl mx-auto px-4 animate-pulse">
          <div className="h-8 w-32 bg-muted rounded mb-8" />
          <div className="h-48 bg-muted rounded-xl mb-8" />
        </div>
      </div>
    );
  }

  if (!list) {
    return (
      <div className="min-h-screen bg-background pt-20 flex items-center justify-center">
        <div className="text-center">
          <LayoutList className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <h2 className="text-xl font-semibold">List not found</h2>
        </div>
      </div>
    );
  }

  const filteredAlbums = getFilteredAlbums();
  const listAlbums = list.albums || [];
  const listUser = list.user || {};
  const collaboratorDetails = list.collaborator_details || [];

  return (
    <div className="min-h-screen bg-[#0A0510] pt-36 pb-12" data-testid="list-detail-page">
      <div className="fixed inset-0 bg-gradient-to-br from-purple-950/20 via-transparent to-purple-900/10 pointer-events-none" />
      
      <div className="relative max-w-4xl mx-auto px-4">
        <Button variant="ghost" onClick={() => navigate(-1)} className="mb-6 -ml-2">
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <div className="glass-card rounded-xl p-6 mb-8">
          <div className="flex items-start justify-between mb-4">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h1 className="text-2xl md:text-3xl font-bold text-white">{list.name}</h1>
                {list.is_public ? (
                  <Globe className="w-5 h-5 text-white/50" />
                ) : (
                  <Lock className="w-5 h-5 text-white/50" />
                )}
              </div>
              {list.description && <p className="text-white/60 mb-4">{list.description}</p>}
            </div>
          </div>

          {/* Owner + Collaborators */}
          <div className="flex items-center gap-3 flex-wrap">
            <Link to={`/profile/${listUser.user_id}`} className="flex items-center gap-2">
              <Avatar className="h-8 w-8 border border-white/10">
                <AvatarImage src={listUser.picture} alt={listUser.name} />
                <AvatarFallback className="bg-primary/20 text-primary text-sm">
                  {listUser.name ? listUser.name.charAt(0).toUpperCase() : "?"}
                </AvatarFallback>
              </Avatar>
              <div>
                <span className="text-sm font-medium text-white hover:text-purple-400 transition-colors">
                  {listUser.name}
                </span>
                <p className="text-xs text-white/50">{listAlbums.length} albums</p>
              </div>
            </Link>

            {collaboratorDetails.length > 0 && (
              <>
                <span className="text-white/30 text-sm">+</span>
                {collaboratorDetails.map((collab) => (
                  <div key={collab.user_id} className="flex items-center gap-1.5 group" data-testid={`collab-${collab.user_id}`}>
                    <Link to={`/profile/${collab.user_id}`} className="flex items-center gap-1.5">
                      <Avatar className="h-6 w-6 border border-purple-500/30">
                        <AvatarImage src={collab.picture} alt={collab.name} />
                        <AvatarFallback className="bg-purple-500/20 text-purple-400 text-xs">
                          {collab.name?.charAt(0)?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                      <span className="text-xs text-white/60 hover:text-purple-400 transition-colors">{collab.name}</span>
                    </Link>
                    {isOwner && (
                      <button
                        onClick={() => removeCollaborator(collab.user_id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity text-white/30 hover:text-red-400"
                        data-testid={`remove-collab-${collab.user_id}`}
                      >
                        <X className="w-3 h-3" />
                      </button>
                    )}
                  </div>
                ))}
              </>
            )}

            {isOwner && (
              <Dialog open={collabDialogOpen} onOpenChange={setCollabDialogOpen}>
                <DialogTrigger asChild>
                  <button
                    className="flex items-center gap-1 text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 transition-colors"
                    data-testid="add-collaborator-btn"
                  >
                    <UserPlus className="w-3 h-3" />
                    Invite
                  </button>
                </DialogTrigger>
                <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-sm">
                  <DialogHeader>
                    <DialogTitle className="text-white">Invite Collaborators</DialogTitle>
                  </DialogHeader>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-white/30" />
                    <Input
                      placeholder="Search users..."
                      value={collabSearch}
                      onChange={(e) => { setCollabSearch(e.target.value); searchUsers(e.target.value); }}
                      className="pl-10 bg-white/5 border-white/10 text-white placeholder:text-white/40"
                      data-testid="collab-search-input"
                    />
                  </div>
                  <div className="space-y-1 max-h-60 overflow-y-auto">
                    {searchingCollabs && <p className="text-xs text-white/40 text-center py-2">Searching...</p>}
                    {collabResults.map((u) => (
                      <div
                        key={u.user_id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                        onClick={() => addCollaborator(u.user_id)}
                        data-testid={`collab-result-${u.user_id}`}
                      >
                        <Avatar className="h-8 w-8">
                          <AvatarImage src={u.picture} alt={u.name} />
                          <AvatarFallback className="bg-purple-500/20 text-purple-400 text-sm">
                            {u.name?.charAt(0)?.toUpperCase()}
                          </AvatarFallback>
                        </Avatar>
                        <span className="text-sm text-white flex-1">{u.name}</span>
                        <UserPlus className="w-4 h-4 text-purple-400" />
                      </div>
                    ))}
                    {!searchingCollabs && collabSearch && collabResults.length === 0 && (
                      <p className="text-xs text-white/40 text-center py-4">No users found</p>
                    )}
                  </div>
                </DialogContent>
              </Dialog>
            )}

            {isCollaborator && (
              <span className="text-xs px-2 py-1 rounded-full bg-purple-500/10 text-purple-400">
                <Users className="w-3 h-3 inline mr-1" />
                Collaborator
              </span>
            )}
          </div>
        </div>

        {canEdit && (
          <div className="mb-6">
            <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
              <DialogTrigger asChild>
                <Button className="rounded-full" data-testid="add-album-to-list-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add Albums
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#1a1a2e] border-white/10 max-w-lg max-h-[80vh] overflow-hidden flex flex-col">
                <DialogHeader>
                  <DialogTitle className="text-white">Add Albums to List</DialogTitle>
                </DialogHeader>
                <div className="py-2">
                  <Input
                    placeholder="Search albums..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="bg-white/5 border-white/10 text-white placeholder:text-white/40 focus:border-purple-500"
                  />
                </div>
                <div className="flex-1 overflow-y-auto space-y-2 pr-2">
                  {filteredAlbums.length > 0 ? (
                    filteredAlbums.map((album) => (
                      <div
                        key={album.album_id}
                        className="flex items-center gap-3 p-2 rounded-lg hover:bg-white/5 cursor-pointer transition-colors"
                        onClick={() => handleAddAlbum(album.album_id)}
                      >
                        <img src={album.cover_url} alt={album.title} className="w-12 h-12 rounded object-cover" />
                        <div className="flex-1 min-w-0">
                          <p className="font-medium truncate text-white">{album.title}</p>
                          <p className="text-sm text-white/60 truncate">{album.artist}</p>
                        </div>
                        <Plus className="w-4 h-4 text-white/50" />
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-white/50 py-8">No albums available</p>
                  )}
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}

        {listAlbums.length > 0 ? (
          <div className="album-grid">
            {listAlbums.map((album) => (
              <div key={album.album_id} className="relative group">
                <AlbumCard album={album} />
                {canEdit && (
                  <button
                    onClick={() => handleRemoveAlbum(album.album_id)}
                    className="absolute top-2 right-2 p-2 rounded-full bg-black/70 backdrop-blur-sm text-white opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500"
                    data-testid={`remove-album-${album.album_id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="glass-card rounded-xl p-12 text-center">
            <LayoutList className="w-12 h-12 text-white/30 mx-auto mb-4" />
            <h3 className="text-lg font-semibold mb-2 text-white">This list is empty</h3>
            <p className="text-white/60">
              {canEdit ? "Add some albums to get started!" : "No albums have been added yet."}
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
